import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight, Sparkles, MapPin } from "lucide-react";
import { HERO_IMAGE, SITE } from "@/data/site";

const headlineLines = ["Stay.", "Sip.", "Socialize."];
const colorForLine = ["text-white", "text-[#A7D38B]", "text-[#f5f0ea] italic"];

export default function Hero({ onBook, onExplore }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Parallax: background drifts slower than content; content fades + rises slightly on scroll
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{ y: bgY, scale: bgScale }}
      >
        <img
          src={HERO_IMAGE}
          alt="Gangtok night skyline with prayer flags"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-[#0a0a0a]" />
        <div className="absolute inset-0 grain" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-40 pb-40 min-h-[100svh] flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="inline-flex w-fit items-center gap-2 rounded-full glass px-3.5 py-1.5 text-xs uppercase tracking-[0.22em] text-[#A7D38B]"
          data-testid="hero-eyebrow"
        >
          <Sparkles size={14} className="text-[#f5f0ea]" />
          New · Gangtok's premium social stay
        </motion.div>

        {/* Masked line-by-line reveal — the signature on-load moment */}
        <h1
          data-testid="hero-headline"
          className="mt-6 font-display font-bold text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tighter"
        >
          {headlineLines.map((line, idx) => (
            <span
              key={line}
              className="block overflow-hidden pb-[0.08em]"
              aria-label={line}
            >
              <motion.span
                className={`block ${colorForLine[idx]}`}
                initial={{ y: "110%", rotate: 3 }}
                animate={{ y: "0%", rotate: 0 }}
                transition={{
                  duration: 1.0,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.15 + idx * 0.14,
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-6 max-w-xl text-neutral-200 text-base sm:text-lg leading-relaxed"
          data-testid="hero-subtext"
        >
          A boutique social stay tucked in the Himalayas. Private rooms with
          mountain views, scenic dorms, rooftop sundowners, bonfire nights and a
          Tibetan-inspired bar — built for the people you haven't met yet.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <button
            onClick={onBook}
            data-testid="hero-book-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-[#62794d] text-[#f5f0ea] px-6 py-3.5 font-semibold text-sm hover:bg-[#7a9261] active:scale-[0.97] transition shadow-[0_10px_40px_rgba(98,121,77,0.45)]"
          >
            Book Your Stay
            <ArrowDownRight
              size={18}
              className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition"
            />
          </button>
          <button
            onClick={onExplore}
            data-testid="hero-explore-btn"
            className="inline-flex items-center gap-2 rounded-full glass px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 active:scale-[0.97] transition"
          >
            Explore Experience
          </button>

          <div className="flex items-center gap-2 ml-1 text-xs text-neutral-300">
            <MapPin size={14} className="text-[#A7D38B]" />
            {SITE.locationShort}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-14 hidden md:grid grid-cols-3 max-w-xl gap-6"
          data-testid="hero-stats"
        >
          {[
            { v: "16", l: "Curated rooms" },
            { v: "12", l: "Oct experiences" },
            { v: "4.9", l: "Guest rating" },
          ].map((s) => (
            <div key={s.l} className="text-left">
              <div className="font-display text-3xl font-bold text-white">{s.v}</div>
              <div className="text-xs uppercase tracking-[0.22em] text-neutral-400">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center text-neutral-400 text-[10px] tracking-[0.3em] uppercase"
      >
        Scroll
        <motion.span
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="mt-2 w-px h-8 bg-gradient-to-b from-white/70 to-transparent origin-top"
        />
      </motion.div>
    </section>
  );
}
